/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 *
 * Singleton for the google maps api. Used to ensure that the google maps api is loaded once when needed.
 * Also, the instance of google maps should be the same in order to avoid the creation of multiple different maps
 * in the application or unnecessary reloads.
 */
import { RGBAColor } from '@deck.gl/core';
import { GoogleMapsOverlay as DeckOverlay } from '@deck.gl/google-maps';
import { GeoJsonLayer } from '@deck.gl/layers';
import React, { CSSProperties, useCallback, useEffect } from 'react';
import { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import chroma from 'chroma-js';
import { ContourType } from '../../shared/entities/beam';
import { performCutout } from '../../utils/BeamUtils';

export type GeoJSONMapOverlayProps = {
  id: string;
  children?: React.ReactNode;
  data: FeatureCollection<MultiPolygon | Polygon>;
  linkType?: 'rl' | 'fl';
  blendColor?: any;
  blendOpacity?: number;
  fillColor: number[] | ((feature: any) => number[]);
  lineColor: number[] | ((feature: any) => number[]);
  highlightColor: number[] | ((feature: any) => number[]);
  beamHighlightColor: number[] | ((feature: any) => number[]);
  map: google.maps.Map;
  hidden?: boolean;
  tooltipStyle?: CSSProperties;
  disableTooltip?: boolean;
  disableBeamHover?: boolean | ((contourType: ContourType) => boolean);
  disableBeamContourHover?: boolean | ((contourType: ContourType) => boolean);
  tooltipText?: (feature: any) => string;
  featureSortFunction?: (
    feature1: Feature<Polygon | MultiPolygon>,
    feature2: Feature<Polygon | MultiPolygon>,
  ) => number;
  disableBeamCutoutForFeaturePairs?: (
    feature1: Feature<Polygon | MultiPolygon>,
    feature2: Feature<Polygon | MultiPolygon>,
  ) => boolean;
  isCutoutEnabled?: boolean;
};

/**
 * This component represents a GeoJSON overlay on a map. This will most likely be used to put
 * a beam on the map.
*/
function GeoJSONMapOverlay(props: GeoJSONMapOverlayProps, ref: React.Ref<HTMLDivElement>) {
  const {
    map,
    id,
    data,
    linkType,
    blendColor,
    blendOpacity,
    fillColor,
    lineColor,
    highlightColor,
    hidden,
    tooltipStyle,
    disableTooltip,
    disableBeamHover,
    disableBeamContourHover,
    tooltipText = (feature: any) => (feature.properties || {}).displayName,
    featureSortFunction,
    disableBeamCutoutForFeaturePairs,
    isCutoutEnabled,
  } = props;
  const [overlay, setOverlay] = React.useState<any>(undefined);
  const [pointerX, setPointerX] = React.useState(0);
  const [pointerY, setPointerY] = React.useState(0);

  const [hoveredObject, setHoveredObject] = React.useState({} as any);
  const [initialLoaded, setInitialLoaded] = React.useState(false);

  const deckLayersCallback = useCallback(
    (
      layerId: string,
      sortFunc?: (feature1: Feature<Polygon | MultiPolygon>, feature2: Feature<Polygon | MultiPolygon>) => number,
      disableBeamCutoutFunc?: (
        feature1: Feature<Polygon | MultiPolygon>,
        feature2: Feature<Polygon | MultiPolygon>,
      ) => boolean,
    ) => {
      let featureCollectionData = data;

      if (isCutoutEnabled) {
        featureCollectionData = performCutout(featureCollectionData, sortFunc, disableBeamCutoutFunc);
      }
      // TODO: Build this as a map where the keys are the beam name and the values is an object with 2 propeties, the min and max, so that the saturation of the contours can be relative to the beam itself
      const lowestBps = data.features.reduce(
        (lowestRate: number, currentFeature: Feature<MultiPolygon | Polygon>) =>
          (linkType === 'rl' ? currentFeature.properties?.rlDataRateBps : currentFeature.properties?.flDataRateBps) <
          lowestRate
            ? linkType === 'rl'
              ? currentFeature.properties?.rlDataRateBps
              : currentFeature.properties?.flDataRateBps
            : lowestRate,
        Infinity,
      );
      const greatestBps = data.features.reduce(
        (greatestRate: number, currentFeature: Feature<MultiPolygon | Polygon>) =>
          (linkType === 'rl' ? currentFeature.properties?.rlDataRateBps : currentFeature.properties?.flDataRateBps) >
          greatestRate
            ? linkType === 'rl'
              ? currentFeature.properties?.rlDataRateBps
              : currentFeature.properties?.flDataRateBps
            : greatestRate,
        -Infinity,
      );

      const colorAlgorithm = (color: any, bps: number, min: any, max: any, enableBlend: boolean) => {
        const gradientStopColor = chroma(color.slice(0, 3)).alpha(1).darken(4).saturate(3);
        const colorScale = chroma.scale([chroma(color.slice(0, 3)), gradientStopColor]).domain([min, max]);
        const mixedColor = colorScale(bps);
        let finalColor = [...mixedColor.rgb(), color[3]];
        if (enableBlend && blendColor) {
          const mixedToAverageMapBackgroundColorScale = chroma.scale([chroma(blendColor).alpha(1), mixedColor]);
          const mixedColorBlended = mixedToAverageMapBackgroundColorScale(blendOpacity);
          finalColor = [...mixedColorBlended.rgb(), 255];
        }

        return finalColor;
      };
      const highlightFunc = (picked: any) => {
        const { object: feature } = picked;
        if (!feature) {
          return [0, 0, 0, 0];
        }
        const color = highlightColor instanceof Function ? highlightColor(feature) : highlightColor;
        const bps = linkType === 'rl' ? feature.properties.rlDataRateBps : feature.properties.flDataRateBps;

        if (!bps) {
          return color;
        }

        return colorAlgorithm(color, bps, lowestBps, greatestBps, true);
      };
      const colorFunc = (colorType: number[] | ((feature: any) => number[])) => (feature: any) => {
        const color = colorType instanceof Function ? colorType(feature) : colorType;
        const bps = linkType === 'rl' ? feature.properties.rlDataRateBps : feature.properties.flDataRateBps;

        if (!bps) {
          return color;
        }

        return colorAlgorithm(color, bps, lowestBps, greatestBps, false);
      };

      let contourFeatureCollections = [featureCollectionData];
      let contourLayers = [];
      let useContourType = false;

      // use contour type hover functionality if provided
      if (disableBeamHover instanceof Function || disableBeamContourHover instanceof Function) {
        const contourToFeatureMap: { [key: string]: Feature<Polygon | MultiPolygon>[] } = {};
        useContourType = true;

        featureCollectionData.features.forEach((feature: Feature<Polygon | MultiPolygon>) => {
          if (!contourToFeatureMap[feature.properties?.contourType]) {
            contourToFeatureMap[feature.properties?.contourType] = [];
          }

          contourToFeatureMap[feature.properties?.contourType].push(feature);
        });

        contourFeatureCollections = Object.keys(contourToFeatureMap).map(
          cType =>
            ({ type: 'FeatureCollection', features: contourToFeatureMap[cType] } as FeatureCollection<
              Polygon | MultiPolygon
            >),
        );
      }

      contourLayers = contourFeatureCollections.reduce(
        (accum: GeoJsonLayer[], featureCollection: FeatureCollection<Polygon | MultiPolygon>) => {
          const contourTypeForFeatureCollection = useContourType
            ? featureCollection.features[0].properties?.contourType
            : '';

          // polygon layer
          accum.push(
            new GeoJsonLayer({
              id: `${layerId}-${contourTypeForFeatureCollection}polygons`,
              data: featureCollection,

              // Styles
              widthUnits: 'pixels',
              lineWidthScale: 1,
              lineWidthMinPixels: 1,
              stroked: true,
              filled: true,

              // Interactive props
              pickable:
                disableBeamHover instanceof Function
                  ? !disableBeamHover(contourTypeForFeatureCollection)
                  : !disableBeamHover,
              onHover: (feature: any) => {
                setHoveredObject(feature.object || {});
                setPointerX(feature.x || 0);
                setPointerY(feature.y || 0);
              },
              autoHighlight: true,

              getFillColor: colorFunc(fillColor),
              getLineColor: colorFunc(lineColor),
              highlightColor: highlightFunc,
            }),
          );

          // polygon contour layer
          accum.push(
            new GeoJsonLayer({
              id: `${layerId}-${contourTypeForFeatureCollection}contourLines`,
              data: featureCollection,
              // Styles
              widthUnits: 'pixels',
              lineWidthScale: 1,
              lineWidthMinPixels: 3,
              stroked: true,
              filled: false,
              // Interactive props
              pickable:
                disableBeamContourHover instanceof Function
                  ? !disableBeamContourHover(contourTypeForFeatureCollection)
                  : !disableBeamContourHover,
              autoHighlight: true,
              getLineColor: [0, 0, 0, 0] as RGBAColor,
              highlightColor: highlightFunc,
              // Interactive props
              onHover: (feature: any) => {
                setHoveredObject(feature.object || {});
                setPointerX(feature.x || 0);
                setPointerY(feature.y || 0);
              },
            }),
          );

          return accum;
        },
        [],
      );
      return contourLayers;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableBeamContourHover, disableBeamHover, isCutoutEnabled, linkType, data],
  );

  // Create overlay
  useEffect(() => {
    if (map && !initialLoaded) {
      const deckOverlay = new DeckOverlay({
        pickingRadius: 8, // 8px picking radius for artifacts. https://deck.gl/docs/api-reference/core/deck#pickingradius
        controller: true,
        glOptions: {
          preserveDrawingBuffer: true, // https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/getContext
        },
      });

      setOverlay(deckOverlay);
      setInitialLoaded(true);
    }
  }, [map, data, id, initialLoaded, setOverlay]);

  // Update the deck gl component
  useEffect(() => {
    if (overlay) {
      overlay.setProps({
        layers: deckLayersCallback(id, featureSortFunction, disableBeamCutoutForFeaturePairs),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckLayersCallback, overlay]);

  // Toggle visibility
  useEffect(() => {
    if (overlay) {
      if (hidden) {
        overlay.setMap(null);
      } else {
        overlay.setMap(map);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay, hidden]);

  return hoveredObject && !disableTooltip && Object.keys(hoveredObject).length > 0 ? (
    <div
      style={{
        position: 'absolute',
        zIndex: 5,
        pointerEvents: 'none',
        left: pointerX,
        top: pointerY,
        backgroundColor: rgbToHex(highlightColor instanceof Function ? highlightColor(hoveredObject) : highlightColor),
        color: '#FFFFFF',
        fontSize: '12px',
        padding: '4px 8px',
        whiteSpace: 'nowrap',
        borderRadius: '12px',
        ...tooltipStyle,
      }}
    >
      {tooltipText(hoveredObject)}
    </div>
  ) : null;
}

/**
 * @deprecated This component is deprecated and should not be used.
 * Add Deck.gl layers directly to Deck.gl overlay located within MapOverlays.
*/
const _GeoJSONMapOverlay = React.forwardRef(GeoJSONMapOverlay);
export { _GeoJSONMapOverlay as GeoJSONMapOverlay };

export const hexToRgb = (hex: string) => {
  let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

export const rgbaFromHex = (hex: string, opacity = 255): RGBAColor => {
  const hexAsRgb = hexToRgb(hex);
  const rgbAArray: RGBAColor = [hexAsRgb?.r, hexAsRgb?.g, hexAsRgb?.b, opacity];
  return rgbAArray;
};

export const componentToHex = (c: number) => {
  let hex = c.toString(16);

  return hex.length === 1 ? '0' + hex : hex;
};

export const rgbToHex = (rgb: number[]) => {
  return `#${componentToHex(rgb[0])}${componentToHex(rgb[1])}${componentToHex(rgb[2])}`;
};

export const getColorFromDb = (name: string, opacity = 255): RGBAColor => {
  if (!name) {
    return [255, 255, 255, 255];
  }

  const dbWithK = name.split('G/T:')[1];
  const dbRaw = dbWithK.split('dB/K')[0];
  const db = parseInt(dbRaw);
  let hex = '#FFFFFF';
  if (db > 6) {
    hex = '#FF0000';
  } else if (db > 0) {
    hex = '#ff782b';
  } else {
    hex = '#f5ff48';
  }
  return rgbaFromHex(hex, opacity);
};

export const dataRateFromName = (name: string): string => {
  if (!name) {
    return 'N/A';
  }
  const dbWithK = name.split('G/T:')[1];
  const dbRaw = dbWithK.split('dB/K')[0];
  const db = parseInt(dbRaw);
  if (db > 6) {
    return '1024 kbps';
  } else if (db > 0) {
    return '512 kbps';
  } else {
    return '64 kbps';
  }
};

export const getNorthCoordinate = (coordinates: any[]) => {
  let indexOfMostNorth = 0;
  let greatestLat = -91;
  coordinates.forEach((coordinate: any[], index: number) => {
    const thisLat = coordinate[1];
    if (thisLat > greatestLat) {
      greatestLat = thisLat;
      indexOfMostNorth = index;
    }
  });
  return coordinates[indexOfMostNorth];
};

export const getSouthCoordinate = (coordinates: any[]) => {
  let indexOfMostSouth = 0;
  let lowestLat = 91;
  coordinates.forEach((coordinate: any[], index: number) => {
    const thisLat = coordinate[1];
    if (thisLat < lowestLat) {
      lowestLat = thisLat;
      indexOfMostSouth = index;
    }
  });
  return coordinates[indexOfMostSouth];
};

export const getEastCoordinate = (coordinates: any[]) => {
  let indexOfMostEast = 0;
  let greatestLon = -181;
  coordinates.forEach((coordinate: any[], index: number) => {
    const thisLon = coordinate[0];
    if (thisLon > greatestLon) {
      greatestLon = thisLon;
      indexOfMostEast = index;
    }
  });
  return coordinates[indexOfMostEast];
};

export const getWestCoordinate = (coordinates: any[]) => {
  let indexOfMostWest = 0;
  let greatestLon = 181;
  coordinates.forEach((coordinate: any[], index: number) => {
    const thisLon = coordinate[0];
    if (thisLon < greatestLon) {
      greatestLon = thisLon;
      indexOfMostWest = index;
    }
  });
  return coordinates[indexOfMostWest];
};
